import classNames from 'classnames'
import { DataModel } from 'legacy/lut/classes/models/DataModel'
import { PhoneInput } from 'legacy/lut/components/common/phoneInput/PhoneInput'
import { isAppLut } from 'legacy/lut/functions'
import { Dropdown } from 'legacy/lut/library/dropdown'
import { DropdownDynamic } from 'legacy/lut/library/dropdown'

import { Checkbox } from 'core-components/library/checkbox'
import { Grid, Item } from 'core-components/library/grid'
import { Input } from 'core-components/library/inputs'
import { MultiSelect } from 'core-components/library/multiSelect'
import { Controller, isEmpty, ValidateInput } from 'core-components/library/validation'
import { ValidationStatus } from 'core-components/library/validation'
import { Static } from 'core-components/types'
import { Crud } from 'core/types/Crud'
import { MY_CONNECT_TYPE } from 'dialer/classes/models/MyConnectType'
import {
  CALL_MODE,
  CallablePhoneWrapper,
} from 'dialer/components/widgets/public/callPhoneWrapper/CallablePhoneWrapper'

import styles from '../ContactEditDialog.module.scss'
import { dropdownRenderFunction } from '../helpers/dropdownRenderFunction'
import { isNewContact } from '../helpers/isNewContact'
import { HPRoles } from '../hpRoles/HPRoles'
import { IContactEditDialogProps, IContactEditDialogState } from '../models'

interface IEditProfileProps {
  contact: DataModel
  checkFieldValidation: (field: string) => boolean | undefined
  changeCountyCode: () => void
  setValidation: (value: boolean) => void
  setChangeStatus: (value: boolean) => void
  setPreferredContact: ({ current }: { current: number }) => void
  contactEditDialogState: IContactEditDialogState
  contactEditDialogProps: IContactEditDialogProps
  checkEmailValidation: (withUniq?: boolean) => Promise<void>
  checkPhoneNumberValidation: (field: string) => void
  onChangeField: (fieldName: string, value?: string | number | boolean, silent?: boolean) => void
}

export const EditProfile = ({
  contact,
  onChangeField,
  checkPhoneNumberValidation,
  checkEmailValidation,
  contactEditDialogState,
  contactEditDialogProps,
  checkFieldValidation,
  changeCountyCode,
  setValidation,
  setChangeStatus,
  setPreferredContact,
}: IEditProfileProps) => {
  const { preferredContact, regionDisabled, readOnly } = contactEditDialogState
  const contactId = contact.get('ContactID', '')

  const callablePhoneWrapperAdditionalAttrs = {
    partnerId: contact.get('PartnerID'),
    contactProcessedInScout: isAppLut(),
  }

  const countriesCodeFromProps = contactEditDialogState.countriesCode || {}
  const countriesCodeItems = countriesCodeFromProps['items'] || []
  const countriesCode = (countriesCodeItems[0] || {}).codes

  const isDisabled = readOnly || contact.get('WorkflowStatus') === 'WIP'

  const HPHero = (contact.get('HPHero') || '').toString()
  const findedCountry = contactEditDialogState.countries.find(
    (el) => el.value === contact.get('CountryOfBusiness')
  )
  const countryValue =
    (findedCountry && findedCountry.name) || contact.get('CountryOfBusiness') || ''

  const userStatus = contact.get('UserStatus')
  const isDisabledNeverEmail =
    isDisabled ||
    !contactEditDialogState.canModifyNeverEmail ||
    (userStatus === 'G_UPP_Partner_Admin_User' || userStatus === 'G_UPP_Partner_User'
      ? contact.get('NeverEmail') === 'Y'
      : userStatus !== 'Contact')

  return (
    <>
      <Controller
        onValidationStatusChange={({ status }) => {
          if (status !== ValidationStatus.WIP) {
            setValidation(status === ValidationStatus.Valid)
          }
        }}
      >
        <Grid gap={8}>
          <Item xs={12} sm={6} md={4}>
            <Dropdown
              label="Salutation *"
              disabled={isDisabled}
              value={contact.get('Salutation') || ''}
              onChange={(value) => onChangeField('Salutation', value)}
              caption="name"
              source={
                new Crud({
                  fetcher: new Static(contactEditDialogProps.salutations),
                  keyField: 'value',
                })
              }
            />
          </Item>
          <Item xs={12} sm={6} md={4}>
            <ValidateInput
              id="cmp-firstname"
              classes={{
                validatorClasses: {
                  root: 'textfield-focus-firstname',
                },
              }}
              disabled={isDisabled}
              label="First Name * (Required)"
              margin={'sm'}
              checkFn={isEmpty}
              value={contact.get('FirstName') || ''}
              onChange={(val) => onChangeField('FirstName', val)}
            />
          </Item>
          <Item xs={12} sm={6} md={4}>
            <ValidateInput
              id="cmp-lastname"
              checkFn={isEmpty}
              classes={{
                validatorClasses: {
                  root: 'textfield-focus-lastname',
                },
              }}
              disabled={isDisabled}
              label="Last Name * (Required)"
              margin={'sm'}
              value={contact.get('LastName') || ''}
              onChange={(val) => onChangeField('LastName', val)}
            />
          </Item>
          <Item xs={12} sm={6} md={4}>
            <Dropdown
              label="Seniority *"
              disabled={isDisabled}
              value={contact.get('Seniority') || ''}
              onChange={(value) => onChangeField('Seniority', value)}
              caption="Name"
              source={
                new Crud({ fetcher: new Static(contactEditDialogProps.seniority), keyField: 'Id' })
              }
            />
          </Item>
          {countriesCode && (
            <>
              <Item xs={12} sm={6} md={4}>
                <CallablePhoneWrapper
                  callMode={CALL_MODE.byIcon}
                  phone={contact.get('MobilePhoneFull') || ''}
                  type={MY_CONNECT_TYPE.PartnerContact}
                  additionalAttributes={callablePhoneWrapperAdditionalAttrs}
                >
                  <PhoneInput
                    isDisabled={isDisabled || regionDisabled}
                    label="Mobile Phone"
                    phoneCodeValue={contact.get('MobilePhone_Prefix')}
                    phoneNumberValue={contact.get('MobilePhone')}
                    onPhoneCodeChange={(val) => onChangeField('MobilePhone_Prefix', val)}
                    onPhoneNumberChange={(val) => {
                      onChangeField('MobilePhone', val)
                      checkPhoneNumberValidation('MobilePhone')
                    }}
                  />
                </CallablePhoneWrapper>
              </Item>
              <Item xs={12} sm={6} md={4}>
                <CallablePhoneWrapper
                  callMode={CALL_MODE.byIcon}
                  phone={contact.get('WorkPhoneFull') || ''}
                  type={MY_CONNECT_TYPE.PartnerContact}
                  additionalAttributes={callablePhoneWrapperAdditionalAttrs}
                >
                  <PhoneInput
                    checkPhoneCodeFn={isNewContact(contactId) ? isEmpty : undefined}
                    checkPhoneNumberFn={async () =>
                      isNewContact(contactId) && checkFieldValidation('WorkPhone')
                        ? 'Incorrect value'
                        : ''
                    }
                    isDisabled={isDisabled || regionDisabled}
                    label="Work Phone * (Required)"
                    phoneCodeValue={contact.get('WorkPhone_Prefix')}
                    phoneNumberValue={contact.get('WorkPhone')}
                    onPhoneCodeChange={(val) => onChangeField('WorkPhone_Prefix', val)}
                    onPhoneNumberChange={(val) => {
                      onChangeField('WorkPhone', val)
                      checkPhoneNumberValidation('WorkPhone')
                    }}
                  />
                </CallablePhoneWrapper>
              </Item>
            </>
          )}
          <Item xs={12} sm={6} md={4}>
            <DropdownDynamic
              id="cmp-countryofbusiness"
              label="Country Of Business * (Required)"
              source={contactEditDialogState.contriesCrud}
              classes={{
                controlClasses: {
                  root: classNames('autocomplete-focus-countryofbusiness'),
                },
              }}
              caption={'name'}
              value={countryValue}
              onChange={async (value) => {
                await onChangeField('CountryOfBusiness', value)
                changeCountyCode()
              }}
              checkFn={isEmpty}
              margin={'sm'}
              disabled={isDisabled || regionDisabled}
            />
          </Item>
          <Item xs={12} sm={6} md={4}>
            <Dropdown
              label="Preferred Language * (Required)"
              disabled={isDisabled}
              value={contact.get('PreferredLanguage') || ''}
              onChange={(value, item) => {
                const name = item?.name
                onChangeField('PreferredLanguage', name)
              }}
              checkFn={isEmpty}
              caption="name"
              source={contactEditDialogState.preferredLanguageSource}
            />
          </Item>
          <Item xs={12} sm={6} md={4}>
            <Dropdown
              label="Status *"
              disabled={isDisabled}
              value={contact.get('Status') || ''}
              onChange={(value) => {
                onChangeField('Status', value)
                if (value === 'Inactive') {
                  setChangeStatus(true)
                }
              }}
              caption="title"
              source={contactEditDialogState.statusSource}
            />
          </Item>
          <Item xs={12} sm={6} md={4}>
            <Input
              id="cmp-email"
              disabled={isDisabled}
              label="Email * (Required)"
              classes={{
                root: 'textfield-focus-email',
              }}
              margin={'sm'}
              value={contact.get('Email') || ''}
              onChange={async (val) => {
                onChangeField('Email', val)
                await checkEmailValidation()
              }}
            />
          </Item>
          <Item xs={12} sm={6} md={4}>
            <Dropdown
              label="Never Email *"
              disabled={isDisabledNeverEmail}
              value={contact.get('NeverEmail') || ''}
              onChange={(value) => onChangeField('NeverEmail', value)}
              caption="title"
              source={contactEditDialogState.neverEmailSource}
            />
          </Item>
          <Item xs={12} sm={6} md={4}>
            <Dropdown
              label="Never Call *"
              disabled={
                isDisabled ||
                ['G_UPP_Partner_Admin_User', 'G_UPP_Partner_User'].includes(
                  contact.get('UserStatus')
                )
              }
              value={contact.get('NeverCall') || ''}
              onChange={(value) => onChangeField('NeverCall', value)}
              caption="title"
              source={contactEditDialogState.neverEmailSource}
            />
          </Item>
          <Item xs={12} sm={6} md={6}>
            <HPRoles isDisabled={isDisabled} entryIdItem={contact} onChangeField={onChangeField} />
          </Item>
          <Item xs={12} sm={6} md={6}>
            <MultiSelect
              label={'Job Functions *'}
              value={(contact.get('PrimaryJobRole') || '').split(';')}
              source={contactEditDialogState.LOVdata['primaryJobRoles']}
              caption={'value'}
              disabled={isDisabled}
              onChange={(value) => onChangeField('PrimaryJobRole', Array.from(value).join(';'))}
            />
          </Item>
        </Grid>
      </Controller>
      {contactEditDialogState.fieldsWithAttention.length > 0 && (
        <>
          <hr className={styles.divider} />
          <div className={styles.attentionText}>
            Please note that the fields highlighted in yellow are missing important profile
            information. You can proceed with your changes but we advise you to review and correct
            the entire profile.
          </div>
        </>
      )}
      <hr className={styles.divider} />

      <Grid gap={8}>
        <Item xs={12} sm={6} md={3}>
          <Input
            id="cmp-photo"
            label="Photo Link"
            margin={'sm'}
            classes={{ leftSide: styles.startContent }}
            disabled={contactEditDialogState.readOnly}
            value={contact.get('Photo') || ''}
            onChange={(val) => onChangeField('Photo', val)}
            startContent={<i className="hpicon hpicon-status-profiling" />}
          />
        </Item>
        <Item xs={12} sm={6} md={3}>
          <Input
            id="cmp-linkedin"
            classes={{ leftSide: styles.startContent }}
            label="LinkedIn Link"
            margin={'sm'}
            disabled={contactEditDialogState.readOnly}
            value={contact.get('LinkedInLink') || ''}
            onChange={(val) => onChangeField('LinkedInLink', val)}
            startContent={<i className="hpicon hpicon-social-linkedin" />}
          />
        </Item>
        <Item xs={12} sm={6} md={3}>
          <Input
            id="cmp-facebook"
            label="Facebook Link"
            disabled={contactEditDialogState.readOnly}
            margin={'sm'}
            classes={{ leftSide: styles.startContent }}
            value={contact.get('FacebookLink') || ''}
            onChange={(val) => onChangeField('FacebookLink', val)}
            startContent={<i className="hpicon hpicon-social-facebook" />}
          />
        </Item>
        <Item xs={12} sm={6} md={3}>
          <Input
            id="cmp-twitter"
            label="Twitter Link"
            disabled={contactEditDialogState.readOnly}
            margin={'sm'}
            classes={{ leftSide: styles.startContent }}
            value={contact.get('TwitterLink') || ''}
            onChange={(val) => onChangeField('TwitterLink', val)}
            startContent={<i className="hpicon hpicon-social-twitter" />}
          />
        </Item>
        <Item xs={12} sm={6} md={3}>
          <Input
            id="cmp-whatsapp"
            margin={'sm'}
            disabled={contactEditDialogState.readOnly}
            label="WhatsApp Link"
            classes={{ leftSide: styles.startContent }}
            value={contact.get('WhatsAppLink') || ''}
            onChange={(val) => onChangeField('WhatsAppLink', val)}
            startContent={<i className="hpicon hpicon-mindset-none" />}
          />
        </Item>
        <Item xs={12} sm={6} md={3}>
          <Dropdown
            disabled={contactEditDialogState.readOnly}
            label="Relationship Strength"
            value={contact.get('RelationshipStrength') || ''}
            onChange={(val) => onChangeField('RelationshipStrength', val)}
            margin={'sm'}
            renderFunction={dropdownRenderFunction}
            caption={'Hint'}
            source={
              new Crud({ fetcher: new Static(contactEditDialogProps.relationship), keyField: 'Id' })
            }
          />
        </Item>
        <Item xs={12} sm={6} md={3}>
          <Dropdown
            label="Change Mindset"
            disabled={contactEditDialogState.readOnly}
            value={contact.get('ChangeMindset') || ''}
            onChange={(value) => onChangeField('ChangeMindset', value)}
            margin={'sm'}
            renderFunction={dropdownRenderFunction}
            caption={'Hint'}
            source={
              new Crud({
                fetcher: new Static(contactEditDialogProps.changeMindset),
                keyField: 'Id',
              })
            }
          />
        </Item>
        <Item xs={12} sm={6} md={3}>
          <Dropdown
            label="Role"
            disabled={contactEditDialogState.readOnly}
            value={contact.get('Role') || ''}
            onChange={(val: string) => onChangeField('Role', val)}
            margin={'sm'}
            renderFunction={dropdownRenderFunction}
            caption={'Hint'}
            source={
              new Crud({
                fetcher: new Static(contactEditDialogProps.roles),
                keyField: 'Id',
              })
            }
          />
        </Item>
        <Item xs={12} sm={6} md={3}>
          <Dropdown
            label="Contact Status"
            disabled={contactEditDialogState.readOnly}
            value={contact.get('ContactStatus') || ''}
            onChange={(val: string) => onChangeField('ContactStatus', val)}
            margin={'sm'}
            renderFunction={dropdownRenderFunction}
            caption={'Hint'}
            source={
              new Crud({
                fetcher: new Static(contactEditDialogProps.status),
                keyField: 'Id',
              })
            }
          />
        </Item>

        {preferredContact.default !== -1 ? (
          <Item xs={12} sm={6} md={3}>
            <div className={styles.checkLabel}>Preferred Contact</div>
            <Checkbox
              label="Yes"
              checked={!!preferredContact.current}
              disabled={contactEditDialogState.readOnly}
              onChange={() =>
                setPreferredContact({
                  ...preferredContact,
                  current: +!preferredContact.current,
                })
              }
            />
          </Item>
        ) : null}
        <Item xs={12} sm={6} md={3}>
          <div className={styles.checkLabel}>HP Hero</div>
          <Checkbox
            checked={!!HPHero}
            label={HPHero ? 'Yes' : 'No'}
            disabled={contactEditDialogState.readOnly}
            onChange={(e: any) => onChangeField('HPHero', Number(e.target.checked))}
          />
        </Item>
        <Item xs={12} sm={6} md={3}>
          <MultiSelect
            label={'Competitors'}
            value={JSON.parse(contact.get('Competitors') || '[]')}
            source={contactEditDialogState.LOVdata['Competitors']}
            disabled={contactEditDialogState.readOnly}
            caption={'value'}
            onChange={(value) => onChangeField('Competitors', JSON.stringify(value))}
          />
        </Item>
      </Grid>
      <p>
        Fields marked with * represents fields updatable in master data system. Please note it may
        take a 1 - 2 days for changes to be reflected in upstream systems
      </p>
    </>
  )
}
